import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { locations } from '@/pages/location/locationData';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://mowandsnow.ca';

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/locations`,
  name: 'Locations — Mow and Snow GTA Service Areas',
  description: 'Mow and Snow serves 11+ cities across the Greater Toronto Area with lawn care, snow removal, yard cleanups, and hardscaping. Find your city and get a free quote.',
  url: `${siteUrl}/locations`,
  isPartOf: { '@id': `${siteUrl}/#website` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Locations', item: `${siteUrl}/locations` },
    ],
  },
};

const itemListSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: locations.map((loc, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'Place',
      name: loc.name,
      description: loc.description,
      url: `${siteUrl}/locations/${loc.slug}`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: loc.name,
        addressRegion: 'ON',
        addressCountry: 'CA',
      },
    },
  })),
};

export default function LocationsHubPage() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />
      <title>Locations | Mow and Snow — GTA Lawn Care & Snow Removal</title>
      <meta name="description" content="Mow and Snow serves 11+ cities across the GTA with lawn mowing, snow removal, yard cleanups, and hardscaping. Find your city and get a free quote today." />
      <meta name="keywords" content="lawn care GTA, snow removal GTA, landscaping Toronto area, yard cleanup Newmarket Aurora Richmond Hill, hardscaping Ontario" />
      <meta property="og:title" content="Locations | Mow and Snow — GTA Lawn Care & Snow Removal" />
      <meta property="og:description" content="Find your city. Mow and Snow provides lawn care, snow removal, and hardscaping across 11+ GTA cities." />
      <link rel="canonical" href={`${siteUrl}/locations`} />
      <meta name="robots" content="index, follow" />

      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-black text-white overflow-hidden">
          <div className="absolute inset-0 bg-gray-900 opacity-20">
            <img
              src="https://public.readdy.ai/ai/img_res/ade0ba5e4436da2bfb80fc43cb28a9ca.jpg"
              alt="GTA residential neighborhoods aerial view"
              className="w-full h-full object-cover object-top"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90"></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-white/50 text-sm mb-6 flex-wrap">
              <Link to="/" className="flex items-center gap-1 hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-home-4-line"></i>
                <span>Home</span>
              </Link>
              <i className="ri-arrow-right-s-line text-white/30"></i>
              <span className="text-yellow-400 font-semibold whitespace-nowrap">Locations</span>
            </nav>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Service <span className="text-yellow-400">Areas</span>
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mb-8">
              Mow and Snow proudly serves homeowners across the Greater Toronto Area. 
              From Newmarket to Oshawa, we deliver reliable lawn care, snow removal, 
              yard cleanups, and hardscaping — all on one simple monthly plan.
            </p>

            <div className="flex flex-wrap items-center gap-6 text-white/70">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center bg-yellow-400/20 rounded-full">
                  <i className="ri-map-pin-2-line text-yellow-400 text-lg"></i>
                </div>
                <span className="font-medium">11+ Cities Served</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center bg-green-400/20 rounded-full">
                  <i className="ri-shield-check-line text-green-400 text-lg"></i>
                </div>
                <span className="font-medium">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center bg-cyan-400/20 rounded-full">
                  <i className="ri-calendar-check-line text-cyan-400 text-lg"></i>
                </div>
                <span className="font-medium">Year-Round Service</span>
              </div>
            </div>
          </div>
        </section>

        {/* Cities Grid */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Find Your City
              </h2>
              <p className="text-gray-500">
                Click your city to see local services, neighbourhoods we cover, and get a free quote.
              </p>
            </div>
            <a
              href="tel:+16479837924"
              className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400 text-gray-900 font-bold rounded-full hover:bg-yellow-500 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-lg"></i>
              Call (647) 983-7924
            </a>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {locations.map((loc) => (
              <Link
                key={loc.slug}
                to={`/locations/${loc.slug}`}
                className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-yellow-300 hover:shadow-lg transition-all cursor-pointer"
              >
                {/* Card Image */}
                <div className="relative h-40 overflow-hidden bg-gray-800">
                  <img
                    src={loc.heroImage}
                    alt={`${loc.name} lawn care and snow removal`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-4 right-4">
                    <h3 className="text-xl font-bold text-white group-hover:text-yellow-300 transition-colors">
                      {loc.name}
                    </h3>
                    <p className="text-white/80 text-sm">{loc.province}</p>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-5">
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                    {loc.description}
                  </p>

                  {/* Service Tags */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {['Lawn', 'Snow', 'Cleanups', 'Hardscaping'].map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 group-hover:bg-yellow-100 group-hover:text-yellow-700 transition-colors whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Neighbourhoods Preview */}
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs text-gray-400 mb-2">Neighbourhoods:</p>
                    <p className="text-xs text-gray-500 line-clamp-1">
                      {loc.neighborhoods.slice(0, 3).join(', ')}
                      {loc.neighborhoods.length > 3 && ` +${loc.neighborhoods.length - 3} more`}
                    </p>
                  </div>

                  {/* CTA */}
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm font-semibold text-yellow-600 group-hover:text-yellow-700 transition-colors">
                      View Services
                    </span>
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-100 group-hover:bg-yellow-400 transition-colors">
                      <i className="ri-arrow-right-line text-yellow-600 group-hover:text-gray-900 transition-colors"></i>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Coverage Map Hint */}
          <div className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-gray-900 to-black text-white">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-3">
                  Not sure if we cover your area?
                </h3>
                <p className="text-white/70 max-w-2xl">
                  We serve the entire Greater Toronto Area including surrounding towns. 
                  If you do not see your city listed, give us a call — we likely service your neighbourhood too.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+16479837924"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-yellow-400 text-gray-900 font-bold rounded-full hover:bg-yellow-500 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-lg"></i>
                  Call Now
                </a>
                <Link
                  to="/#contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 border border-white/30 text-white font-semibold rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line text-lg"></i>
                  Get a Quote
                </Link>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-16 grid sm:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-2xl bg-gray-50 border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-yellow-100 rounded-full">
                <i className="ri-map-2-line text-2xl text-yellow-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">11+</p>
              <p className="text-gray-500">Cities Served</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-gray-50 border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-green-100 rounded-full">
                <i className="ri-home-4-line text-2xl text-green-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">500+</p>
              <p className="text-gray-500">Happy Homeowners</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-gray-50 border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-cyan-100 rounded-full">
                <i className="ri-calendar-check-line text-2xl text-cyan-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">365</p>
              <p className="text-gray-500">Days of Service</p>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}